/*
 * WHAT THE LEAD BLOCK, THE LATEST CONVERSATION AND THE STORY SAY, worked out once.
 *
 * The fourteen lead blocks (record/lead/*.js) are drawn in one card grammar (frames
 * 1a, 1c, 1d, 1g, 1i): a small grey label left, meta right, an 18px headline, its
 * sub-line, then sections under a rule. Every sentence in those cards is a reading
 * of the payloads the shell already holds, and a sentence assembled inside JSX is
 * one nobody can test: "Touch 2 of 3", "Viewed Sep 30 by Tom Becker · 3 views",
 * "Shahar wrote “done” at 21:50", "Notice Jul 31 · service ended 30 days later".
 * So the components only lay these out, and this module, pure and pinned by
 * test/record-lead-model.test.js, decides what they say.
 *
 * WHAT IT NEVER DOES: decide the situation (record/situation.mjs), the Next lines
 * (nextModel.mjs) or the score (scoreView.mjs). It takes their answers as inputs,
 * so a block can never disagree with the header about where the company stands.
 *
 * THE HONESTY RULES it holds to, each from the reality check (scratchpad/check/
 * reality.txt) or the brief:
 *   - a lead's times are always "{theirs} · {yours}" (zones.mjs bothZones), and a
 *     colleague's message is dated in THEIR zone when the payload knows it;
 *   - PandaDoc's state only from its notification emails: no count until there are
 *     two views, never "no email arrived" (only "no signed copy on record");
 *   - nothing counts redlines or names a role nobody stored;
 *   - "not recorded" where a field is empty, never a guess dressed as a fact.
 */
import { shortDate, relativeDay, clock, daysBetween, addDays, plural, firstName, fullName, dateRange, toDate, listWords, enumWords } from "../format.mjs";
import { bothZones, personZones } from "../zones.mjs";
import { whoLine, isCalendarMail } from "../timelineModel.mjs";
import { cleanBodyFolded, summarize, quoteOnRecord, callSummaryPoints, callActionItems, gmailThreadUrl, normalizeSubject } from "../../timelineModel.mjs";
import { lastTransition, setByWords, STAGE_LABELS, hasSignedCopy } from "../situation.mjs";
import { REASON_LABELS } from "../endings.mjs";
import { feeLines } from "../moneyModel.mjs";
import { formAnswers, formMessage, latestFormSubmission } from "../../formAnswers.mjs";
import { isMeetingNote } from "../meetingNotes.mjs";

const MS_MIN = 60000;

/* ------------------------------------------------------------ small readers */

/*
 * NO EM DASHES IN ANY COPY (the house rule). The agreement builder's citations and
 * warnings are server strings (src/agreement/*.js) that were written before the
 * rule and carry " — "; the record prints them through this rather than editing
 * eleven sentences in a module the CLI also reads.
 */
export function noDash(text) {
  if (text === null || text === undefined) return text;
  return String(text)
    .replace(/\s+[—–]\s+/g, ", ")
    .replace(/[—]/g, ", ");
}

const dirOf = (row) => (row && (row.direction || (row.metadata && row.metadata.direction))) || null;
const isMail = (row) => row && row.subtype === "email" && !isCalendarMail(row) && !(row.metadata && row.metadata.bulk);
const time = (ts) => {
  const d = toDate(ts);
  return d ? d.getTime() : 0;
};

/*
 * The body as a person reads it: the quoted chain and the detected signature gone.
 * `signer` (the person who wrote it) also folds a sign-off block the signature
 * detector misses: a last paragraph that opens with their own name ("Ella Moss /
 * Legal counsel, Northwind Data"), which frame 1d does not show. A name on the
 * same paragraph as the words ("done / Shahar", frame 1a) stays.
 */
export function readable(text, { signer = null } = {}) {
  const { body } = cleanBodyFolded(text || "");
  const clean = String(body || "").trim();
  const names = signer ? [fullName(signer), firstName(signer)].filter(Boolean).map((n) => n.toLowerCase()) : [];
  if (!names.length) return clean;
  const paras = clean.split(/\n\s*\n/);
  if (paras.length < 2) return clean;
  const last = paras[paras.length - 1].split("\n").map((l) => l.trim()).filter(Boolean);
  const opens = last.length && last.length <= 4 && names.includes(last[0].replace(/[,.]$/, "").toLowerCase());
  return opens ? paras.slice(0, -1).join("\n\n").trim() : clean;
}

/** The first line written, at most `max` characters, with an ellipsis when cut. */
export function opening(text, max = 90) {
  const line = readable(text)
    .split("\n")
    .map((l) => l.trim())
    .find(Boolean);
  return line ? summarize(line, max) : "";
}

/*
 * A title inside a running line: "Ella Moss · legal counsel". Lower-cased only when
 * it reads as words ("Legal counsel"), never an acronym ("CFO", "VP Marketing"),
 * which the second letter tells apart.
 */
export function titleWords(title) {
  const s = String(title || "").trim();
  if (!s) return "";
  if (s.length > 1 && s[1] === s[1].toLowerCase() && s[1] !== s[1].toUpperCase()) return s.charAt(0).toLowerCase() + s.slice(1);
  return s;
}

/** Everyone the record can name: the company's people, and the person on screen. */
function peopleOf(company, contact) {
  const list = [...(((company && company.contacts) || []).filter(Boolean))];
  if (contact && !list.some((p) => String(p.id) === String(contact.id))) list.push(contact);
  return list;
}

/** The contact with this id, or null. */
export function personById(company, contact, id) {
  if (!id) return null;
  return peopleOf(company, contact).find((p) => String(p.id) === String(id)) || null;
}

/*
 * "Lukasz, Zoli" from the Cc addresses a send stored (metadata.cc_emails, PR #155).
 * A company contact reads as their first name; our own team are not contacts, so
 * their address's local part stands in, capitalised ("lukasz@remotion.io").
 */
export function ccNames(emails, company = null) {
  const people = (company && company.contacts) || [];
  return (Array.isArray(emails) ? emails : [])
    .filter(Boolean)
    .map((e) => {
      const hit = people.find((p) => p.email && String(p.email).toLowerCase() === String(e).toLowerCase());
      if (hit) return firstName(hit);
      const local = String(e).split("@")[0].split(/[._+-]/)[0];
      return local ? local.charAt(0).toUpperCase() + local.slice(1) : null;
    })
    .filter(Boolean);
}

/*
 * The zones to date one person's message in. A colleague's own zone when the
 * payload carries one (company.contacts[].local_zone); otherwise the record's,
 * which comes from the same company for everyone but a HubSpot-zoned contact.
 */
export function zonesFor(person, zones) {
  return personZones(person, zones);
}

/** "Sep 30 · 10:40 ET · 17:40 your time", or "Sep 30 · 21:50" when the clocks agree. */
export function whenLine(ts, zones, { now = new Date() } = {}) {
  if (!toDate(ts)) return "";
  const z = bothZones(ts, zones, {});
  return [shortDate(ts, { now }), z && z.text].filter(Boolean).join(" · ");
}

/** "just now", "4 min ago", within the hour; the full date and both zones after it. */
export function freshOrWhen(ts, zones, { now = new Date() } = {}) {
  const mins = Math.floor((time(now) - time(ts)) / MS_MIN);
  if (mins >= 0 && mins < 1) return "just now";
  if (mins >= 1 && mins < 60) return `${mins} min ago`;
  return whenLine(ts, zones, { now });
}

/** "today", "tomorrow", "in 3 days", then the date: how far off a call is. */
export function untilWords(ts, { now = new Date() } = {}) {
  const n = daysBetween(now, ts);
  if (n === null) return "";
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  if (n > 1 && n <= 7) return `in ${n} days`;
  return shortDate(ts, { now });
}

/** "5 days ago", "yesterday", "today". */
export function agoWords(ts, { now = new Date() } = {}) {
  const n = daysBetween(ts, now);
  if (n === null) return "";
  if (n <= 0) return "today";
  if (n === 1) return "yesterday";
  return `${n} days ago`;
}

/* ------------------------------------------------------------ the conversation */

/*
 * WHO WROTE A MESSAGE, as the conversation card names them: the person in full
 * ("Ella Moss"), their title when they are not the person whose record this is
 * (the header already says Shahar is CEO), and the zones to date it in. Outbound
 * is "You → Ella, Cc Priya": the people holding a copy, then the Cc we stored.
 */
function authorOf(row, { company, contact, zones }) {
  const w = whoLine(row);
  const direction = dirOf(row);
  if (direction === "outbound") {
    const holders = Array.isArray(row.holders) ? row.holders : [];
    const to = holders.map((h) => firstName(h)).filter(Boolean);
    const own = !to.length && (row.contact_first_name || row.contact_last_name) ? firstName({ first_name: row.contact_first_name, last_name: row.contact_last_name }) : null;
    const cc = ccNames((row.metadata || {}).cc_emails, company);
    const names = to.length ? to : own ? [own] : [];
    const head = names.length ? `You → ${names.join(", ")}` : "You";
    const recipient = holders[0] ? personById(company, contact, holders[0].contact_id) : personById(company, contact, row.contact_id);
    return { name: cc.length ? `${head}, Cc ${cc.join(", ")}` : head, short: "You", title: null, zones: zonesFor(recipient, zones), person: recipient, direction };
  }
  const senderId = (row.sender && row.sender.contact_id) || (w.who !== "inbound" && Array.isArray(row.holders) && row.holders.length === 1 ? row.holders[0].contact_id : null) || (w.who !== "inbound" && !row.holders ? row.contact_id : null);
  const person = personById(company, contact, senderId);
  const name = person ? fullName(person) : row.sender ? fullName(row.sender) : w.who || "inbound";
  const short = person ? firstName(person) : w.sender || w.who || "inbound";
  const isRecordPerson = person && contact && String(person.id) === String(contact.id);
  return {
    name,
    short,
    title: person && !isRecordPerson && person.job_title ? titleWords(person.job_title) : null,
    zones: zonesFor(person, zones),
    person,
    direction,
  };
}

const threadOf = (row) => {
  const id = row && row.metadata && row.metadata.thread_id;
  if (id) return `t:${id}`;
  const s = normalizeSubject(row && row.title);
  return s ? `s:${s.toLowerCase()}` : null;
};

/**
 * LATEST CONVERSATION (frames 1a, 1d, 1i "after send"), worked out:
 *
 *   { mode: "theirs" | "sent", lead, quote, after, before, thread, unanswered }
 *
 *   lead     the message the card is about, at reading size: their last message
 *            (frame 1a's "done", 1d's redlines), or ours when they never wrote
 *            ("Our last email, unanswered", matrix 1e chasing);
 *   quote    the colleague who brought them in, when the message before theirs on
 *            the same thread was someone else at the company (1d: "Priya, Sep 30:
 *            Looping in Ella for legal.");
 *   after    "Since then · You: Hey Shahar, welcome to Remotion…": the newest
 *            message anywhere at the company, when it is newer than theirs;
 *   before   in "sent" mode (a send from this page: the message tops the
 *            conversation, 1i), what it answered: "Before it · Ella: Two redlines…";
 *   thread   { subject, count, range, url }: the company-wide count of the thread
 *            their message is on, and "Open in Gmail ↗".
 *
 * `justSent` is true while the shell holds a receipt from this page, which is the
 * only way to tell "we just answered" from "we wrote this morning".
 */
export function conversationView(rows, { company = null, contact = null, zones = null, justSent = false, now = new Date() } = {}) {
  const mail = (rows || []).filter(isMail).sort((a, b) => time(b.occurred_at) - time(a.occurred_at));
  if (!mail.length) return null;
  const ctx = { company, contact, zones };
  const newest = mail[0];
  const lastIn = mail.find((r) => dirOf(r) === "inbound") || null;
  const sent = !!justSent && dirOf(newest) === "outbound";
  const leadRow = sent ? newest : lastIn || newest;

  const view = (row, max = null) => {
    const a = authorOf(row, ctx);
    const meta = row.metadata || {};
    return {
      id: row.id,
      at: row.occurred_at,
      who: a.name,
      short: a.short,
      title: a.title,
      direction: a.direction,
      when: sent && row === newest ? freshOrWhen(row.occurred_at, a.zones, { now }) : whenLine(row.occurred_at, a.zones, { now }),
      // Their words in full; ours by the first line (the card is about what THEY
      // said, and our own email in full would repeat the quote above it).
      body: max ? opening(row.body, max) : a.direction === "outbound" ? opening(row.body, 160) : readable(row.body, { signer: a.person }),
      subject: row.title || "",
      snippet: meta.body_kind === "snippet",
      thread_id: meta.thread_id || null,
      row,
    };
  };

  const key = threadOf(leadRow);
  const thread = key ? mail.filter((r) => threadOf(r) === key) : [leadRow];
  const oldest = thread[thread.length - 1];

  // The colleague before them on the thread: someone else at the company, within
  // two weeks, so a months-old message is not dressed up as the introduction.
  let quote = null;
  if (!sent && dirOf(leadRow) === "inbound") {
    const leadAuthor = authorOf(leadRow, ctx);
    const prior = thread.find(
      (r) =>
        r !== leadRow &&
        time(r.occurred_at) < time(leadRow.occurred_at) &&
        time(leadRow.occurred_at) - time(r.occurred_at) <= 14 * 86400000 &&
        dirOf(r) === "inbound" &&
        authorOf(r, ctx).short !== leadAuthor.short &&
        authorOf(r, ctx).short !== "inbound"
    );
    if (prior) {
      const p = authorOf(prior, ctx);
      quote = { who: p.short, at: prior.occurred_at, day: shortDate(prior.occurred_at, { now }), text: opening(prior.body, 120) };
    }
  }

  const after = !sent && newest !== leadRow && time(newest.occurred_at) > time(leadRow.occurred_at) ? view(newest, 56) : null;
  if (after && after.direction === "outbound") after.who = "You";

  let before = null;
  if (sent) {
    const answered = mail.find((r) => r !== newest && dirOf(r) === "inbound" && time(r.occurred_at) <= time(newest.occurred_at));
    if (answered) {
      const b = authorOf(answered, ctx);
      before = { who: b.short, text: opening(answered.body, 60), at: answered.occurred_at };
    }
  }

  const lead = sent ? view(leadRow, 110) : view(leadRow);
  return {
    mode: sent ? "sent" : "theirs",
    lead,
    quote,
    after,
    before,
    unanswered: !sent && dirOf(leadRow) === "outbound" && !lastIn,
    thread: {
      subject: leadRow.title || "",
      count: thread.length,
      range: dateRange(oldest.occurred_at, thread[0].occurred_at, { now }),
      url: gmailThreadUrl((leadRow.metadata || {}).thread_id),
    },
  };
}

/*
 * THE WHITE BUTTON ON LATEST CONVERSATION, for the email Next has no line for.
 *
 * Next owns the actions, so when it already holds a composer line for whoever
 * wrote last there is nothing here. But Next only carries what the server raises,
 * and some conversations it never asks about: a person with no company ("are you
 * taking on new clients?" has no Next at all), a client's question, a line hidden
 * with its ×. The old thread card always offered Reply or Nudge, so dropping it
 * left those records with no way to write from them. So, by who wrote last:
 *   - they did          → Reply (reply_waiting), for anyone not read-only;
 *   - we did, unanswered → Nudge (nudge_due), on prospects only (the brief's
 *                          Latest conversation detail, coverage-situations [16]).
 * Never on the read-only summary (churned, lost), never just after a send, and a
 * white button: the screen's one yellow stays Next's.
 *   → { kind, label, contact_id, age_days } | null
 */
export function conversationAction(view, { next = null, company = null, state = "open", now = new Date() } = {}) {
  if (!view || view.mode === "sent" || state === "summary") return null;
  const newest = (view.after && view.after.row) || (view.lead && view.lead.row) || null;
  const cid = newest && newest.contact_id;
  if (!cid) return null;
  const covered = ((next && next.lines) || []).some(
    (l) => l && l.contact_id && String(l.contact_id) === String(cid) && (l.controls || []).some((c) => c && c.id === "compose")
  );
  if (covered) return null;
  const dir = dirOf(newest);
  if (dir === "inbound") return { kind: "reply_waiting", label: "Reply", contact_id: cid, age_days: daysBetween(newest.occurred_at, now) };
  if (dir === "outbound" && company && company.status === "prospect") {
    return { kind: "nudge_due", label: "Nudge", contact_id: cid, age_days: daysBetween(newest.occurred_at, now) };
  }
  return null;
}

/** "Re: Pluto/Remotion followup · 9 messages · Sep 23–Oct 1 · snippet only". */
export function subjectLine(view) {
  if (!view) return "";
  const parts = [];
  if (view.lead.subject) parts.push(view.lead.subject);
  if (view.thread.count > 1) parts.push(plural(view.thread.count, "message"), view.thread.range);
  if (view.unanswered) parts.push("unanswered");
  if (view.lead.snippet) parts.push("snippet only");
  return parts.join(" · ");
}

/* ------------------------------------------------------------ the story */

/** "written 09:39 today", "written yesterday", "written Apr 1". */
export function storyAge(story, { now = new Date() } = {}) {
  const at = story && story.generated_at;
  if (!toDate(at)) return null;
  const day = relativeDay(at, { now });
  if (day === "today") return `written ${clock(at)} today`;
  if (day === "yesterday") return "written yesterday";
  return `written ${shortDate(at, { now })}`;
}

/*
 * "BEFORE THE WELCOME": what has happened since the story last read the record.
 *
 * `newest_at` is the newest item the story READ (src/actions/story.js); the first
 * row after it is what the story cannot know, said by what it is. The welcome and
 * the finance intro are named as such from the onboarding facts (an email within
 * five minutes of the send the checklist counts), so frame 1a's line comes out
 * word for word. On a parked company's wake-up day, a story written before the
 * wake-up says so even when nothing new arrived (coverage check [19]).
 */
export function storyBefore(story, rows, { company = null, contact = null, onboarding = null, wakesAt = null, now = new Date() } = {}) {
  const read = toDate(story && (story.newest_at || story.generated_at));
  if (!read) return null;
  const ob = onboarding || {};
  const near = (a, b) => toDate(a) && toDate(b) && Math.abs(time(a) - time(b)) <= 5 * MS_MIN;
  const newer = (rows || [])
    .filter((r) => r && time(r.occurred_at) > read.getTime() && time(r.occurred_at) <= time(now))
    .filter((r) => !isCalendarMail(r) && !(r.metadata && r.metadata.bulk))
    .filter((r) => !(r.item_type === "stage_transition" && r.contact_id))
    .sort((a, b) => time(a.occurred_at) - time(b.occurred_at));
  const first = newer[0];
  if (first) {
    if (first.subtype === "email") {
      if (dirOf(first) === "outbound") {
        if (ob.welcome && near(first.occurred_at, ob.welcome.at)) return "before the welcome";
        if (ob.finance_intro && near(first.occurred_at, ob.finance_intro.at)) return "before the finance intro";
        const to = Array.isArray(first.holders) && first.holders[0] ? firstName(first.holders[0]) : first.contact_first_name || null;
        return to ? `before your email to ${to}` : "before your last email";
      }
      const a = authorOf(first, { company, contact, zones: null });
      return a.short && a.short !== "inbound" ? `before ${a.short}’s email` : "before their last email";
    }
    if (first.subtype === "call") return "before the call";
    if (first.subtype === "meeting") return "before the meeting";
    if (first.subtype === "form_submission") return "before the form";
    if (first.item_type === "document") return first.event === "signed" ? "before the signature" : "before the latest PandaDoc view";
    if (first.item_type === "stage_transition") {
      if (first.subtype === "status" && first.to_value === "client") return "before the win";
      const label = STAGE_LABELS[first.to_value];
      return label ? `before the move to ${label}` : "before the latest move";
    }
    if (first.subtype === "note") return "before your note";
    return "before the latest entry";
  }
  // The wake-up is not a timeline row, so it is measured against when the story was
  // WRITTEN: a story written this morning already knows today is the wake-up day.
  const wake = toDate(wakesAt);
  const written = toDate(story.generated_at) || read;
  if (wake && wake > written && wake <= toDate(now)) return "before the wake-up";
  if (wake && daysBetween(wake, now) === 0 && daysBetween(written, now) > 0) return "before the wake-up";
  return null;
}

/* ------------------------------------------------------------ new lead */

// The panel's labels for the form's questions (frame 1c), in the frame's order; the
// phone's short ones (1h). formAnswers.mjs names them for every other reader.
const ANSWER_ORDER = [
  { from: "Budget", label: "Monthly LinkedIn budget", short: "Budget" },
  { from: "Running ads", label: "Running LinkedIn ads", short: "Running ads" },
  { from: "Goals", label: "Goals", short: "Goals", list: true },
  { from: "How they heard", label: "How they heard", short: "How they heard" },
  { from: "Referred", label: "Referred", short: "Referred" },
];

/** The form's answers as the panel reads them: [{ key, label, value }]. */
export function answersView(form, { short = false } = {}) {
  const raw = form ? formAnswers(form) : [];
  const out = [];
  for (const o of ANSWER_ORDER) {
    const hit = raw.find((a) => a.label === o.from);
    if (hit) out.push({ key: hit.key, label: short ? o.short : o.label, value: o.list ? String(hit.value).split(" · ").join(", ") : hit.value });
  }
  for (const a of raw) if (!ANSWER_ORDER.some((o) => o.from === a.label)) out.push(a);
  return out;
}

/*
 * "What they told us", for a new lead (frame 1c): the form, named and dated in
 * both zones, its message and answers. With no form, their first email (a warm
 * intro is the usual case: "Dani → You, Shahar"), and with neither, the honest
 * "none, no form" the Profile tab uses.
 */
export function toldUs({ rows = [], company = null, contact = null, zones = null, short = false, now = new Date() } = {}) {
  const form = latestFormSubmission(rows);
  if (form) {
    const meta = form.metadata || {};
    const titled = form.title && !/^website form$/i.test(form.title) ? form.title : null;
    const name = (meta.form && (meta.form.form_name || meta.form.name)) || meta.form_name || titled || "Website form";
    const z = bothZones(form.occurred_at, zones, {});
    return {
      kind: "form",
      // "Get a proposal form · today 07:05 ET · 14:05 your time": the day and the
      // clock read as one phrase (frame 1c).
      meta: [/form$/i.test(name) ? name : `${name} form`, [relativeDay(form.occurred_at, { now }), z && z.text].filter(Boolean).join(" ")].filter(Boolean).join(" · "),
      message: formMessage(form),
      answers: answersView(form, { short }),
    };
  }
  const first = (rows || []).filter(isMail).filter((r) => dirOf(r) === "inbound").sort((a, b) => time(a.occurred_at) - time(b.occurred_at))[0];
  if (first) {
    const w = whoLine(first);
    return {
      kind: "email",
      meta: [`${w.who} · their first email`, whenLine(first.occurred_at, zones, { now })].join(" · "),
      message: readable(first.body),
      answers: [],
    };
  }
  const intro = contact && (contact.lead_channel === "intro_email" || contact.lead_channel_derived === "intro_email");
  return { kind: "none", meta: null, message: null, answers: [], empty: intro ? "None, no form: they came by intro." : "None, no form." };
}

/* ------------------------------------------------------------ chasing */

// The cadence ladder (src/actions/model.js cadenceState): after one touch the next
// rung is a nudge, after two the final check, after three Remove from pipeline.
const RUNGS = {
  1: { key: "cadence_nudge_days", fallback: 4, words: "nudge", due: "nudge_due" },
  2: { key: "cadence_final_days", fallback: 7, words: "final check", due: "final_check_due" },
  3: { key: "cadence_close_days", fallback: 7, words: "remove from pipeline", due: "exhausted" },
};

/**
 * "Touch 2 of 3 · our last email 5 days ago" and "Next: final check Oct 3" (the
 * brief's chasing row), from the server's cadence and thresholds.
 *   { headline, next, due, cold, replied }
 * `cold` is the fade mark (going cold): the last rung has been sent.
 */
export function chaseView(act, { now = new Date() } = {}) {
  const a = act || {};
  const cadence = a.cadence || {};
  const t = a.thresholds || {};
  const facts = a.facts || {};
  if (cadence.state === "replied") {
    return { headline: `They replied ${agoWords(facts.last_inbound_at, { now }) || "since"}`, next: null, due: false, cold: false, replied: true };
  }
  const touches = Math.max(1, Number(cadence.touches) || 1);
  const touch = Math.min(3, touches);
  const rung = RUNGS[touch];
  const last = facts.last_outbound_at || null;
  const at = last ? addDays(last, Number(t[rung.key]) || rung.fallback) : null;
  const due = cadence.state === rung.due || (at && toDate(at) <= toDate(now));
  const ago = last ? agoWords(last, { now }) : null;
  return {
    headline: `Touch ${touch} of 3${ago ? ` · our last email ${ago}` : ""}`,
    next: due ? `Next: ${rung.words}, due now` : at ? `Next: ${rung.words} ${shortDate(at, { now })}` : `Next: ${rung.words}`,
    due: !!due,
    cold: touches >= 3,
    replied: false,
  };
}

/* ------------------------------------------------------------ the quote */

/**
 * WHAT WE QUOTED, verbatim (QuotedPricing.js's rules, kept): the first pricing
 * email's lines, or the newer terms Gabriel approved from a later email
 * (quoteOnRecord, migration 065) — a later email alone never moves them. The
 * SENT date is the first pricing email's, the revision's its own email's, and the
 * stage's own date sits beside them only when it differs, because the move is
 * often approved days after the email.
 *   { intro, terms, sent_at, revised_at, revised_by, first_email, stage_at, thread_id, item } | null
 */
export function quoteView(rows, { stageAt = null, company = null } = {}) {
  const q = quoteOnRecord(rows, company);
  if (!q) return null;
  const day = (ts) => (toDate(ts) ? toDate(ts).toDateString() : null);
  const latest = q.revised_at || q.sent_at;
  // A greeting is not a lead-in ("Hey Leo, here's the pricing:"); "To recap our
  // pricing:" is.
  const intro = q.intro && !/^(hey|hi|hello|dear)\b/i.test(q.intro) ? q.intro.replace(/:$/, "") : null;
  return {
    ...q,
    intro,
    stage_at: stageAt && day(stageAt) !== day(latest) && day(stageAt) !== day(q.sent_at) ? stageAt : null,
  };
}

/* ------------------------------------------------------------ calls */

/** A video link the calendar gave us, never a guess (FocusPanel.js joinLink). */
export function joinLink(meta) {
  const m = meta || {};
  const candidate = m.hangout_link || m.conference_url || m.join_url || m.conference || m.location;
  return typeof candidate === "string" && /^https?:\/\//.test(candidate.trim()) ? candidate.trim() : null;
}

function transcriptOf(meta) {
  const url = meta && (meta.transcript_url || meta.fireflies_url || meta.url);
  return typeof url === "string" && /^https?:\/\//.test(url) ? url : null;
}

const OURS = /gabriel|remotion/i;

/** To-dos split ours and theirs, by the owner Fireflies wrote above each group. */
export function todosOf(raw) {
  const ours = [];
  const theirs = [];
  for (const g of callActionItems(raw)) (g.owner && OURS.test(g.owner) ? ours : theirs).push(...g.items);
  return { ours, theirs };
}

/*
 * WHAT HE PROMISED ON A RECORDED CALL (asked 2026-10-13: "Call summaries should
 * always include any action items - anything I specifically said I'd do, or
 * send - any pricing quoted, anything concrete that I promised or agreed to").
 *
 * Read from the transcript on the server (src/calls/transcript.js), every item
 * backed by a quote that is in the transcript. Grouped for the card:
 *   You promised   what he said he'd send or do
 *   Pricing        every price or term he quoted
 *   Agreed         what both sides settled (the next call, a trial, a start)
 *   Theirs         what they said they'd do
 * → { state, groups: [{ label, items: [{ text, at, quote, key, done_at }] }], coverage }
 *   state  "read"     the reading landed (groups may be empty: nothing promised)
 *          "thin"     Fireflies heard too little of the call to read anything
 *          "unread"   no reading yet (an older call, or one still being read):
 *                     the card falls back to Fireflies' own to-dos
 */
const PROMISE_GROUPS = [
  { label: "You promised", kinds: ["send", "do"] },
  { label: "Pricing", kinds: ["pricing"] },
  { label: "Agreed", kinds: ["agreed"] },
  { label: "Theirs", kinds: ["theirs"] },
];

export function actionItemsOf(commitments) {
  const c = commitments && typeof commitments === "object" ? commitments : null;
  if (!c || !Array.isArray(c.items)) return { state: "unread", groups: [], coverage: null };
  const coverage = c.coverage || null;
  const groups = PROMISE_GROUPS.map((g) => ({
    label: g.label,
    items: c.items
      .filter((i) => i && g.kinds.includes(i.kind) && i.text)
      .map((i) => ({ text: String(i.text), at: Number.isFinite(Number(i.at)) ? Number(i.at) : null, quote: i.quote || null, key: i.key || null, done_at: i.done_at || null })),
  })).filter((g) => g.items.length);
  if (coverage && coverage.thin && !groups.length) return { state: "thin", groups, coverage };
  return { state: "read", groups, coverage };
}

/** "12:04" or "1:02:09" from seconds into a call. */
export function transcriptClock(seconds) {
  const n = Math.max(0, Math.round(Number(seconds) || 0));
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const s = String(n % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}

/** The turn being spoken at `seconds`: the last one that started by then (or the first). */
export function turnIndexAt(turns, seconds) {
  const list = turns || [];
  if (!list.length) return -1;
  let k = 0;
  for (let i = 0; i < list.length; i++) {
    if (Number(list[i].at) <= Number(seconds)) k = i;
    else break;
  }
  return k;
}

/** Indexes of the turns whose words (or speaker) contain the query, ignoring case. */
export function matchingTurns(turns, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return [];
  const out = [];
  (turns || []).forEach((t, i) => {
    if (`${t.s} ${t.t}`.toLowerCase().includes(q)) out.push(i);
  });
  return out;
}

/** "16:00–16:30": a meeting's clock range in one zone. */
function range(start, end, timeZone) {
  const a = clock(start, { timeZone });
  return end && toDate(end) ? `${a}–${clock(end, { timeZone })}` : a;
}

/** "09:00–09:30 ET · 16:00–16:30 your time", or once when the clocks agree. */
export function rangeBothZones(start, end, zones) {
  const z = bothZones(start, zones, {});
  if (!z) return "";
  const mine = zones && zones.mine ? zones.mine.tz : undefined;
  const yours = range(start, end, mine);
  if (!z.theirs || z.same) return z.theirs ? yours : `${yours} your time`;
  const theirTz = zones.theirs.tz;
  const abbr = z.theirs.split(" ").slice(1).join(" ");
  return `${range(start, end, theirTz)}${abbr ? ` ${abbr}` : ""} · ${yours} your time`;
}

/*
 * THE BOOKING BEHIND A BOOKED CALL, when he booked it through his booking page:
 * GET /booking/contact/:id's `next`, matched to the calendar's call by its start
 * (within a minute). Its zone is the one THEY picked on the page, which beats any
 * zone worked out from a country, and is the only one there is for a Gmail lead
 * with no country (contactZone has no booking zone). Also the agenda they chose.
 *   → { zones, agenda } | null; zones is the record's with theirs replaced
 */
export function bookedVia(call, booking, zones) {
  const next = booking && booking.next;
  if (!call || !next || !next.start || !toDate(call.at)) return null;
  if (Math.abs(time(next.start) - time(call.at)) > MS_MIN) return null;
  return {
    zones: next.timezone ? { ...(zones || {}), theirs: { tz: next.timezone } } : zones,
    agenda: next.agenda_label || null,
  };
}

/**
 * EVERY CALL, NEWEST FIRST (the brief's Call held row). A Fireflies call and the
 * calendar meeting it recorded are one entry (within 20 minutes of each other);
 * a meeting with no recording is its own entry and says so, with its Held /
 * No-show answer and any notes written against it (a note carrying its event id).
 *   [{ key, kind: "call"|"meeting", title, at, end_at, minutes, points, todos,
 *      actions (actionItemsOf), transcript, in_crm_transcript, outcome, event_id, notes, attendees, activity_id, contact_id }]
 * `activity_id` is what a follow-up from this entry is written from (POST
 * /actions/reply): the recording's row, or for a meeting nobody recorded the
 * newest notes typed against it; null when there is nothing to write from.
 * Notes typed under a meeting are never a call of their own (meetingNotes.mjs),
 * whichever shape they were written in.
 */
export function callsView(rows, { company = null, now = new Date() } = {}) {
  const list = (rows || []).filter(Boolean);
  const calls = list.filter((r) => r.subtype === "call" && !isMeetingNote(r));
  const meetings = list.filter((r) => r.subtype === "meeting" && time(r.occurred_at) <= time(now));
  const notes = list.filter(isMeetingNote);
  const used = new Set();
  const people = (company && company.contacts) || [];
  const nameFor = (email) => {
    const p = people.find((c) => c.email && String(c.email).toLowerCase() === String(email || "").toLowerCase());
    return p ? firstName(p) : null;
  };
  const attendeesOf = (meta, row) => {
    const fromMeta = (Array.isArray(meta.attendees) ? meta.attendees : []).map((a) => nameFor(a && a.email)).filter(Boolean);
    const fromParts = (Array.isArray(meta.participants) ? meta.participants : []).map(nameFor).filter(Boolean);
    const fromHolders = (Array.isArray(row.holders) ? row.holders : []).map((h) => firstName(h)).filter(Boolean);
    return [...new Set([...fromMeta, ...fromParts, ...fromHolders])];
  };
  const notesFor = (eventId) =>
    notes
      .filter((n) => eventId && String(n.metadata.event_id) === String(eventId))
      .map((n) => ({ id: n.id, at: n.occurred_at, body: String(n.body || "").trim() }))
      .sort((a, b) => time(b.at) - time(a.at));

  const out = calls.map((c) => {
    const meta = c.metadata || {};
    const twin = meetings.find((m) => !used.has(m.id) && Math.abs(time(m.occurred_at) - time(c.occurred_at)) <= 20 * MS_MIN);
    if (twin) used.add(twin.id);
    const mins = Number(meta.duration_minutes);
    const eventId = twin ? twin.event_id || twin.id : null;
    return {
      key: `call-${c.id}`,
      kind: "call",
      title: c.title || (twin && twin.title) || "Call",
      at: c.occurred_at,
      end_at: twin && twin.metadata ? twin.metadata.end_at || null : null,
      minutes: Number.isFinite(mins) && mins > 0 ? Math.round(mins) : null,
      points: callSummaryPoints(c.body || ""),
      todos: todosOf(meta.action_items),
      actions: actionItemsOf(c.commitments),
      transcript: transcriptOf(meta),
      // Read in the CRM (record/TranscriptDialog) when Fireflies recorded it.
      in_crm_transcript: c.source === "fireflies",
      // The call's page in Meetings ▸ Calls, by its Fireflies id.
      call_id: c.source === "fireflies" && c.external_id ? c.external_id : null,
      outcome: twin && twin.outcome ? twin.outcome.outcome : null,
      event_id: eventId,
      notes: notesFor(eventId),
      attendees: attendeesOf(meta, c),
      activity_id: c.id,
      contact_id: c.contact_id || (twin && twin.contact_id) || null,
    };
  });
  for (const m of meetings) {
    if (used.has(m.id)) continue;
    const meta = m.metadata || {};
    const eventId = m.event_id || m.id;
    const typed = notesFor(eventId);
    out.push({
      key: `meeting-${m.id}`,
      kind: "meeting",
      title: m.title || "Meeting",
      at: m.occurred_at,
      end_at: meta.end_at || null,
      minutes: null,
      points: [],
      todos: { ours: [], theirs: [] },
      actions: actionItemsOf(null),
      transcript: null,
      in_crm_transcript: false,
      call_id: null,
      outcome: m.outcome ? m.outcome.outcome : null,
      event_id: eventId,
      notes: typed,
      attendees: attendeesOf(meta, m),
      activity_id: typed.length ? typed[0].id : null,
      contact_id: m.contact_id || null,
    });
  }
  return out.sort((a, b) => time(b.at) - time(a.at));
}

/** "Sep 18 · 32 min · recorded", "Sep 23 · 16:00–16:30 · not recorded · held". */
export function callFacts(entry, zones, { now = new Date() } = {}) {
  if (!entry) return "";
  const parts = [shortDate(entry.at, { now })];
  if (entry.kind === "meeting") parts.push(rangeBothZones(entry.at, entry.end_at, zones));
  if (entry.minutes) parts.push(`${entry.minutes} min`);
  parts.push(entry.kind === "call" ? "recorded" : "not recorded");
  if (entry.outcome) parts.push(entry.outcome === "held" ? "held" : "no-show");
  return parts.filter(Boolean).join(" · ");
}

/**
 * THE NEXT CALL, for Call booked: the meeting row behind facts.next_meeting_at
 * (its end, its join link, who accepted), and the pre-call reminder plan for it.
 *   { at, end_at, title, join, attendees, plan } | null
 */
export function nextCallView({ facts = {}, rows = [], automations = null, company = null, now = new Date() } = {}) {
  const at = facts && facts.next_meeting_at && !facts.next_meeting_declined ? facts.next_meeting_at : null;
  if (!at) return null;
  const future = (rows || []).filter((r) => r && r.subtype === "meeting" && time(r.occurred_at) >= time(now) - 60 * MS_MIN);
  const row =
    future.find((r) => Math.abs(time(r.occurred_at) - time(at)) <= MS_MIN) ||
    future.sort((a, b) => time(a.occurred_at) - time(b.occurred_at))[0] ||
    null;
  const meta = (row && row.metadata) || {};
  const people = (company && company.contacts) || [];
  const attendees = (Array.isArray(meta.attendees) ? meta.attendees : [])
    .map((a) => {
      const p = people.find((c) => c.email && a && a.email && String(c.email).toLowerCase() === String(a.email).toLowerCase());
      return p ? firstName(p) : null;
    })
    .filter(Boolean);
  const eventId = row ? row.event_id || null : null;
  const plans = (automations && automations.plans) || [];
  const plan =
    plans.find((p) => eventId && p.event_id === eventId) ||
    plans.find((p) => p.call_at && Math.abs(time(p.call_at) - time(at)) <= MS_MIN) ||
    null;
  return {
    at,
    end_at: meta.end_at || null,
    title: facts.next_meeting_title || (row && row.title) || "Call",
    join: joinLink(meta),
    attendees,
    plan: plan && plan.status !== "stopped" ? plan : null,
    stopped: plan && plan.status === "stopped" ? plan : null,
  };
}

// What a reminder did (AutomationCard.js OUTCOME_WORDS).
export const REMINDER_WORDS = {
  QUEUED: "queued",
  PENDING: "sent",
  REPLIED: "sent, they replied",
  SCHEDULED: "sent, they booked",
  GHOSTED: "sent, no reply",
  BOUNCED: "bounced",
  BLOCKED: "skipped",
  CANCELLED: "cancelled",
  DRY_RUN: "dry run",
};

/** "Oct 1 · 09:00 ET · 16:00 your time · queued": one reminder of the plan. */
export function reminderLine(rem, zones, { now = new Date() } = {}) {
  const what = REMINDER_WORDS[rem && rem.outcome] || String((rem && rem.outcome) || "planned").toLowerCase();
  return [whenLine(rem && rem.at, zones, { now }), what, rem && rem.blocked_reason ? noDash(rem.blocked_reason) : null].filter(Boolean).join(" · ");
}

/* ------------------------------------------------------------ earlier stays */

const EXIT_WORDS = { recycled: "parked", lost: "lost", not_a_fit: "not a fit", client: "won", churned: "churned", vendor: "a vendor", advisory: "advisory" };

/**
 * "Last time: reached Pricing sent, parked · no budget now" (the brief's prep
 * pack), "First time here" with one stay, null before Feb 2026's stints exist:
 * a record with no stints says nothing rather than claiming a first stay.
 */
export function lastTimeLine(company) {
  const stints = (company && company.pipeline_stints) || [];
  if (!stints.length) return null;
  const prev = stints[1];
  if (!prev) return "First time here";
  const stage = STAGE_LABELS[prev.furthest_stage] || (prev.furthest_stage ? enumWords(prev.furthest_stage) : null);
  const how = EXIT_WORDS[prev.exit_status] || (prev.exit_status ? enumWords(prev.exit_status) : null);
  const why = prev.exit_reason ? (REASON_LABELS[prev.exit_reason] || enumWords(prev.exit_reason)).toLowerCase() : null;
  const head = [stage ? `reached ${stage}` : null, how].filter(Boolean).join(", ");
  return `Last time: ${[head || "an earlier stay", why].filter(Boolean).join(" · ")}`;
}

const ORDINAL = ["first", "second", "third", "fourth", "fifth"];

/** "first stay · got as far as Pricing sent" (frame 1g), null with no stints. */
export function stayLine(company) {
  const stints = (company && company.pipeline_stints) || [];
  if (!stints.length) return null;
  const n = stints.length;
  const furthest = stints[0] && stints[0].furthest_stage ? STAGE_LABELS[stints[0].furthest_stage] || enumWords(stints[0].furthest_stage) : null;
  return `${ORDINAL[n - 1] || `${n}th`} stay${furthest ? ` · got as far as ${furthest}` : ""}`;
}

/* ------------------------------------------------------------ the agreement */

/**
 * THE AGREEMENT'S STATE, only from PandaDoc's notification emails (act.document,
 * PR #154/#155): "Viewed Sep 30 by Tom Becker · 3 views · not signed" with "First
 * viewed Sep 29" under it (frame 1d). One view prints no count: until the log's
 * backfill has run a single view is only a floor (recordFacts.js documentFor).
 *   { headline, sub, url, signed }
 */
export function agreementState(doc, { now = new Date() } = {}) {
  const d = (ts) => shortDate(ts, { now });
  const source = "read only from PandaDoc’s notification emails";
  if (!doc || (!doc.signed_at && !doc.first_viewed_at)) {
    return { headline: "No view or signature on record", sub: [source], url: (doc && doc.notice_url) || null, signed: false };
  }
  const views = Number(doc.views) || 0;
  if (doc.signed_at) {
    return {
      headline: `Signed ${d(doc.signed_at)}`,
      sub: [doc.first_viewed_at ? `First viewed ${d(doc.first_viewed_at)}` : null, source].filter(Boolean),
      url: doc.notice_url || null,
      signed: true,
    };
  }
  const last = doc.last_viewed_at || doc.first_viewed_at;
  return {
    headline: `Viewed ${d(last)}${doc.last_viewer_name ? ` by ${doc.last_viewer_name}` : ""}${views > 1 ? ` · ${plural(views, "view")}` : ""} · not signed`,
    sub: [views > 1 ? `First viewed ${d(doc.first_viewed_at)}` : null, source].filter(Boolean),
    url: doc.notice_url || null,
    signed: false,
  };
}

/*
 * THE LATEST ON THE AGREEMENT'S THREAD (reality.txt [16]): the newest inbound from
 * anyone at the company on the thread that carried the agreement. The thread is the
 * one the move to Agreement + legal was read from (its evidence ref names the Gmail
 * message), else the email we sent within ten minutes of the move. Nothing counts
 * redlines, so the line says who and when, never "2 items".
 *   { id, who, at, text, thread_id } | null
 */
export function agreementLatest(rows, { company = null, contact = null } = {}) {
  const list = (rows || []).filter(Boolean);
  const move = list
    .filter((r) => r.item_type === "stage_transition" && r.subtype === "pipeline_stage" && r.to_value === "agreement")
    .sort((a, b) => time(b.occurred_at) - time(a.occurred_at))[0];
  const mail = list.filter(isMail);
  let thread = null;
  if (move) {
    const ref = move.evidence && move.evidence.evidence_ref ? String(move.evidence.evidence_ref).replace(/^gmail:/, "") : null;
    const byRef = ref ? mail.find((r) => r.msg_id === ref || String(r.external_id || "").split(":")[0] === ref) : null;
    const near = mail.find((r) => dirOf(r) === "outbound" && Math.abs(time(r.occurred_at) - time(move.occurred_at)) <= 10 * MS_MIN);
    const carrier = byRef || near;
    thread = carrier ? threadOf(carrier) : null;
  }
  const since = move ? time(move.occurred_at) : 0;
  const inbound = mail
    .filter((r) => dirOf(r) === "inbound" && (thread ? threadOf(r) === thread : time(r.occurred_at) >= since))
    .sort((a, b) => time(b.occurred_at) - time(a.occurred_at))[0];
  if (!inbound) return null;
  const a = authorOf(inbound, { company, contact, zones: null });
  return { id: inbound.id, who: a.short, at: inbound.occurred_at, text: opening(inbound.body, 90), thread_id: (inbound.metadata || {}).thread_id || null };
}

/* ------------------------------------------------------------ new client */

const DONE_HOW = { mail: "sent", draft: "sent", backfill: "found in Gmail", dismissed: "done by hand" };

/**
 * THE NEW-CLIENT CHECKLIST (frame 1a; the brief's "new-client checklist"). The real
 * rows are receipts or point at their Next line; the five Proposed rows are inert
 * and do not count (reality.txt [25]: "1 of 2 done", never "1 of 7").
 *   { rows: [{ key, done, text, cc?, pointer }], proposed: [...], done, total,
 *     until, collapsed }
 * A receipt is dated in both zones, like every time a lead was on the end of
 * ("Welcome sent Oct 1 · 09:42" once when the clocks agree, as for Pluto).
 * `pointer` is true only while a Next line exists to point at (the welcome's chip
 * lasts 14 days, the situation 30: past that the row says "not on record").
 */
export function onboardingView({ company = null, act = null, rows = [], zones = null, now = new Date() } = {}) {
  const co = company || {};
  const ob = (act && act.onboarding) || {};
  const facts = (act && act.facts) || {};
  const kinds = new Set(((act && act.actions) || []).map((a) => a && a.kind));
  const wonAt = co.won_at || null;
  const d = (ts) => shortDate(ts, { now });
  const mailNear = (at) =>
    (rows || []).find((r) => isMail(r) && dirOf(r) === "outbound" && toDate(at) && Math.abs(time(r.occurred_at) - time(at)) <= 3 * MS_MIN) || null;

  const out = [];
  const welcome = ob.welcome || (facts.kickoff_sent_at ? { at: facts.kickoff_sent_at, how: "mail" } : null);
  if (welcome) {
    const how = DONE_HOW[welcome.how] || "sent";
    const sent = mailNear(welcome.at);
    const cc = sent ? ccNames((sent.metadata || {}).cc_emails, company) : [];
    out.push({
      key: "welcome",
      done: true,
      text: how === "sent" ? `Welcome sent ${whenLine(welcome.at, zones, { now })}` : `Welcome · ${how}${welcome.at ? `, ${d(welcome.at)}` : ""}`,
      cc: cc.length ? `Cc ${cc.join(", ")}` : null,
      pointer: false,
    });
  } else {
    const pointer = kinds.has("kickoff");
    out.push({ key: "welcome", done: false, text: pointer ? `Welcome · owed since ${d(wonAt)}` : "Welcome · not on record", pointer });
  }
  const financeApplies = ob.finance_applies !== undefined ? !!ob.finance_applies : false;
  if (financeApplies) {
    const fi = ob.finance_intro || (facts.finance_intro_sent_at ? { at: facts.finance_intro_sent_at, how: "mail" } : null);
    if (fi) {
      const how = DONE_HOW[fi.how] || "sent";
      out.push({ key: "finance", done: true, text: how === "sent" ? `Finance intro sent ${fi.at ? whenLine(fi.at, zones, { now }) : ""}`.trim() : `Finance intro · ${how}${fi.at ? `, ${d(fi.at)}` : ""}`, pointer: false });
    } else {
      const pointer = kinds.has("finance_intro");
      out.push({ key: "finance", done: false, text: pointer ? `Finance intro · owed since ${d(wonAt)}` : "Finance intro · not on record", pointer });
    }
  }
  // The internal Slack channel, for clients won since it became an item (2026-10-15).
  // Made rather than sent, so it never joins the "until … sent" line below.
  if (ob.slack_applies) {
    const ch = ob.slack_channel || null;
    if (ch) {
      const how = ch.how === "existing" ? "found" : "made";
      out.push({ key: "slack", done: true, text: `Slack channel #${ch.name || ""} · ${how}${ch.created_at ? ` ${d(ch.created_at)}` : ""}`, cc: "you and Lukasz", pointer: false });
    } else {
      const dismissed = !!(facts.action_dismissals && facts.action_dismissals.slack_channel);
      const pointer = kinds.has("slack_channel");
      out.push({ key: "slack", done: dismissed, text: dismissed ? "Slack channel · done by hand" : pointer ? "Slack channel · not made yet" : "Slack channel · not on record", pointer });
    }
  }
  const done = out.filter((r) => r.done).length;
  const owed = out.filter((r) => !r.done && r.key !== "slack").map((r) => (r.key === "welcome" ? "the welcome" : "the finance intro"));
  const thirty = wonAt ? d(addDays(wonAt, 30)) : null;
  const until = owed.length
    ? `until ${listWords(owed)} ${owed.length > 1 ? "are" : "is"} sent${thirty ? `, or ${thirty}` : ""}`
    : thirty
      ? `until ${thirty}`
      : null;
  return {
    rows: out,
    proposed: [
      { key: "kickoff", text: "Kickoff call booked" },
      { key: "access", text: "Ad account and Page access granted" },
      { key: "auditor", text: "Account linked in our auditor" },
      { key: "live", text: "Live", sub: "first impression served, starts the 90-day minimum" },
      { key: "am", text: "Account manager", add: "Add account manager" },
    ],
    done,
    total: out.length,
    until,
    collapsed: out.length > 0 && done === out.length,
  };
}

/**
 * "Closed won Sep 30 · 22:08 · set by you" and "Shahar wrote “done” at 21:50. No
 * signed copy is on record." (frame 1a). The clock is the move's, beside the won
 * DATE only when they fall on the same day (the won date can be picked). The last
 * thing they wrote before it is the newest inbound at or before the move, said by
 * its first line, never called evidence (reality.txt [7]).
 */
export function wonView({ company = null, act = null, rows = [], zones = null, now = new Date() } = {}) {
  const co = company || {};
  const won = lastTransition(co, "status", "client");
  const wonAt = co.won_at || (won && won.occurred_at) || null;
  const sameDay = won && wonAt && daysBetween(won.occurred_at, wonAt) === 0;
  const headline = [wonAt ? `Closed won ${shortDate(wonAt, { now })}` : "Closed won", sameDay ? clock(won.occurred_at) : null, setByWords(won)].filter(Boolean).join(" · ");
  const cut = won ? won.occurred_at : wonAt;
  const before = cut
    ? (rows || [])
        .filter((r) => isMail(r) && dirOf(r) === "inbound" && time(r.occurred_at) <= time(cut) && time(cut) - time(r.occurred_at) <= 7 * 86400000)
        .sort((a, b) => time(b.occurred_at) - time(a.occurred_at))[0]
    : null;
  let wrote = null;
  if (before) {
    const a = authorOf(before, { company, contact: null, zones });
    const z = bothZones(before.occurred_at, a.zones, {});
    const when = daysBetween(before.occurred_at, cut) === 0 ? `at ${z ? z.text : clock(before.occurred_at)}` : `on ${shortDate(before.occurred_at, { now })}`;
    wrote = `${a.short} wrote “${opening(before.body, 60)}” ${when}.`;
  }
  const signed = hasSignedCopy(co, act);
  return { headline, wrote, signed, signedLine: signed ? "A signed copy is on record." : "No signed copy is on record." };
}

/* ------------------------------------------------------------ parked */

const EXTRA_REASONS = { never_engaged: "Never engaged", went_quiet: "Went quiet", no_budget: "No budget" };
const FORMER_LOSS = new Set(["chose_competitor", "declined", "closed_out"]);

/** A stored reason in words, sentence case: "Stopped chasing", "Never engaged". */
export function reasonWords(code) {
  if (!code) return null;
  const s = String(code).trim();
  return REASON_LABELS[s] || EXTRA_REASONS[s] || (s.charAt(0).toUpperCase() + s.slice(1)).replace(/_/g, " ");
}

/**
 * PARKED (frame 1g): "Stopped chasing · since Apr 1" and "Removed from the pipeline
 * by you, remind in 6 months. Wakes today, Oct 1." A park whose reason is a loss's
 * is a former loss and says so; the import's "never engaged" (860 of 1,143 parks)
 * gets its words; with no wake-up date it says so instead of inventing one.
 *   { headline, sub, stay, lastTime, formerLoss }
 */
export function parkedView(company, { now = new Date() } = {}) {
  const co = company || {};
  const row = lastTransition(co, "status", "recycled");
  const reason = reasonWords(co.status_reason) || "No reason recorded";
  const d = (ts) => shortDate(ts, { now });
  const by = row && row.source === "app" ? "Removed from the pipeline by you" : row && (row.source === "automation" || row.source === "auditor") ? "Parked automatically" : row && row.source === "hubspot_import" ? "Parked in the HubSpot import" : "Out of the pipeline";
  const wakes = co.recycle_until || null;
  const months = row && wakes ? Math.round(daysBetween(row.occurred_at, wakes) / 30.4) : null;
  const left = wakes ? daysBetween(now, wakes) : null;
  const wake = !wakes
    ? "No reminder set."
    : left === 0
      ? `Wakes today, ${d(now)}.`
      : left < 0
        ? `Was due to wake ${d(wakes)}.`
        : `Wakes ${d(wakes)}.`;
  return {
    headline: `${reason}${row ? ` · since ${d(row.occurred_at)}` : ""}`,
    sub: `${FORMER_LOSS.has(co.status_reason) ? "A former loss. " : ""}${by}${months && months >= 1 ? `, remind in ${plural(months, "month")}` : ""}. ${wake}`,
    stay: stayLine(co),
    lastTime: (co.pipeline_stints || []).length > 1 ? lastTimeLine(co) : null,
    formerLoss: FORMER_LOSS.has(co.status_reason),
  };
}

/* ------------------------------------------------------------ churned */

// The cleanup's "not in auditor roster" and a bare "churned" are not reasons
// (reality.txt [18]: 34 and 13 of prod's 50 churned companies).
const NOT_A_REASON = new Set(["not in auditor roster", "churned", "not_in_auditor_roster"]);

/**
 * CHURNED (frame 1i, Brightwave and the older churn): built from what Mark
 * churned… stores (endings.mjs writesFor "churn"): churned_at is the end of
 * service, the "Churn notice" timeline note carries the notice date and reason.
 *
 * THE NOTICE COMES FROM THE NOTE OR NOWHERE. Mark churned… always writes the note
 * and always sets churned_at to notice + 30 days, so a churn WITHOUT the note was
 * marked some other way (the cleanup, the import), and its churned_at is not
 * notice + 30: working the notice back from it would print a date that never
 * happened. So with no note the older-churn frame's "Notice · not recorded" is
 * what shows, and `complete` picks that compact form.
 *   { headline, sub, why, fee, tenure, notice: { at, body } | null, complete }
 */
export function churnView(company, rows, { now = new Date() } = {}) {
  const co = company || {};
  const d = (ts) => shortDate(ts, { now });
  const note = (rows || []).find((r) => r && r.subtype === "note" && /churn notice/i.test(String(r.title || ""))) || null;
  const m = note && String(note.body || "").match(/Notice received ([A-Z][a-z]{2} \d{1,2}, \d{4})/);
  const noticeAt = m ? toDate(`${m[1]} 12:00`) : note ? toDate(note.occurred_at) : null;
  const raw = co.status_reason ? String(co.status_reason).trim() : "";
  const why = raw && !NOT_A_REASON.has(raw.toLowerCase()) ? raw.replace(/_/g, " ") : null;
  const fee = feeLines(co.signed_fee_structure);
  const end = co.churned_at || null;
  const months = co.won_at && end ? Math.max(1, Math.round(daysBetween(co.won_at, end) / 30.4)) : null;
  const ended = end && toDate(end) <= toDate(now);
  return {
    headline: `${end ? `Churned ${d(end)}` : "Churned"}${why ? ` · ${why}` : ""}`,
    sub: noticeAt ? `Notice ${d(noticeAt)} · ${ended || !end ? "service ended 30 days later" : `service ends ${d(end)}`}` : "Notice not recorded",
    churnedOn: end ? d(end) : null,
    why,
    fee,
    tenure: co.won_at ? `${dateRange(co.won_at, end || now, { now })}${months ? ` · ${plural(months, "month")}` : ""}` : null,
    notice: note ? { at: noticeAt, day: noticeAt ? d(noticeAt) : null, body: String(note.body || "").trim() } : null,
    complete: !!note,
  };
}

/* ------------------------------------------------------------ lost */

/**
 * LOST (the brief's Lost row; matrix 1e): when and why, and when it parks itself.
 * A loss turns into a park 90 days on (src/watchers/recycle.js), and a re-engage
 * date he set becomes that park's wake-up when it is later than the turn
 * (situation.mjs resolves both dates; this only words them).
 *   { headline, sub, meta }
 */
export function lostView(situation, company, { now = new Date() } = {}) {
  const r = situation || {};
  const co = company || {};
  const d = (ts) => shortDate(ts, { now });
  const why = reasonWords(co.status_reason);
  const later = r.wakes_at && r.parks_at && toDate(r.wakes_at) > toDate(r.parks_at);
  const left = r.parks_at ? daysBetween(now, r.parks_at) : null;
  return {
    headline: `${r.lost_at ? `Lost ${d(r.lost_at)}` : "Lost"}${why ? ` · ${why.toLowerCase()}` : ""}`,
    sub: r.parks_at
      ? `Parks itself on ${d(r.parks_at)}${later ? `, and wakes on ${d(r.wakes_at)}, the date you set` : ", 90 days after the loss"}.`
      : "Parks itself 90 days after the loss.",
    meta: left === null ? null : left > 0 ? `parks itself in ${plural(left, "day")}` : left === 0 ? "parks itself today" : null,
  };
}

/* ------------------------------------------------------------ client */

/** "client since Feb 3 · 7 months", for the account's meta. */
export function clientSince(company, { now = new Date() } = {}) {
  const won = company && company.won_at;
  if (!toDate(won)) return null;
  const months = Math.max(0, Math.round(daysBetween(won, now) / 30.4));
  return `client since ${shortDate(won, { now })}${months ? ` · ${plural(months, "month")}` : ""}`;
}

/** "Sep 2026" from the archive's "2026-09" (AdDelivery.js monthLabel). */
export function monthLabel(m) {
  const [y, mm] = String(m || "").split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const name = names[Number(mm) - 1];
  return y && name ? `${name} ${y}` : String(m || "");
}

/**
 * WHAT OUR ADS REACHED THEM WITH (AdDelivery.js, restyled): reach and engagement,
 * which are per-company facts, never a spend figure (LinkedIn attributes cost only
 * where something was clicked, so it is zero across nearly every row). Null when
 * the archive has nothing, which LinkedIn's privacy floor makes indistinguishable
 * from never reaching them, so the panel is absent rather than zeroed.
 *   { span, metrics: [{ label, value }], months: [{ label, impressions, clicks }] }
 */
export function deliveryView(delivery) {
  if (!delivery || !delivery.impressions) return null;
  const commas = (n) => new Intl.NumberFormat("en-US").format(n || 0);
  const metrics = [
    { label: "Impressions", value: commas(delivery.impressions) },
    { label: "Clicks", value: commas(delivery.clicks) },
  ];
  if (delivery.engagements > 0) metrics.push({ label: "Engagements", value: commas(delivery.engagements) });
  if (delivery.lp_clicks > 0) metrics.push({ label: "Page clicks", value: commas(delivery.lp_clicks) });
  if (delivery.leads > 0) metrics.push({ label: "Leads", value: commas(delivery.leads) });
  const list = delivery.month_list || [];
  const span = delivery.first_month
    ? delivery.first_month === delivery.last_month
      ? monthLabel(delivery.first_month)
      : `${monthLabel(delivery.first_month)}–${monthLabel(delivery.last_month)}`
    : null;
  return {
    span,
    metrics,
    months: [...list].reverse().map((m) => {
      const r = (delivery.months && delivery.months[m]) || {};
      return { key: m, label: monthLabel(m), impressions: commas(r.impressions), clicks: commas(r.clicks) };
    }),
  };
}

/* ------------------------------------------------------------ deciding */

/**
 * DECIDING INTERNALLY (they answered the pricing): the quote and their newest
 * reply after it, the days since, and the fallback the brief asks for: older
 * records reached this stage before Pricing sent existed, so with no quote in the
 * mail the block leads with the days since the call.
 *   { quote, reply: row | null, since, sinceWhat, headline }
 */
export function decidingView({ rows = [], facts = {}, stageAt = null, company = null, now = new Date() } = {}) {
  const quote = quoteView(rows, { stageAt, company });
  const anchor = quote ? quote.sent_at : null;
  const reply =
    (rows || [])
      .filter((r) => isMail(r) && dirOf(r) === "inbound" && (!anchor || time(r.occurred_at) > time(anchor)))
      .sort((a, b) => time(b.occurred_at) - time(a.occurred_at))[0] || null;
  if (quote) {
    const from = reply ? reply.occurred_at : quote.sent_at;
    const since = daysBetween(from, now);
    return { quote, reply, since, sinceWhat: reply ? "their reply" : "the pricing", headline: null };
  }
  const call = facts.last_meeting_at || null;
  const since = call ? daysBetween(call, now) : null;
  return {
    quote: null,
    reply,
    since,
    sinceWhat: call ? "the call" : null,
    headline: call ? `${plural(since, "day")} since the call` : "No call on record",
  };
}
